import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useQuiz } from "@/features/quiz"
import { useQuizStore } from "@/stores/quiz-store"
import { useKeyboardInset } from "../hooks/use-keyboard-inset"
import { Button } from "@/components/ui/button"
import { QuizBottomDecoration } from "./quiz-bottom-decoration"
import { QuizHeader } from "./quiz-header"
import { QuizLegalNote } from "./quiz-legal-note"
import { ShieldCheckIcon } from "./shield-check-icon"
import { resolveStepDescriptor } from "@/features/quiz"
import type { QuizAnswerValue } from "@/features/quiz"
import { trackEvent } from "@/features/analytics"
import { warmPayrails } from "@/features/payments"
import { ensureHandLandmarker } from "@/features/palmistry"
import type { QuizConfig } from "../types"

const PRELOAD_AT_STEP = 2

interface QuizRendererProps {
  config: QuizConfig
  onComplete: () => void
}

export function QuizRenderer({ config, onComplete }: QuizRendererProps) {
  const {
    currentStep,
    currentStepIndex,
    currentValue,
    countedCurrent,
    countedTotal,
    currentCounts,
    isFirst,
    canProceed,
    goNext,
    goBack,
    setCurrentAnswer,
  } = useQuiz(config)
  const answers = useQuizStore((s) => s.answers)
  // On Android the keyboard overlays the layout viewport (no auto-scroll on a
  // full-height step), so reserve its height as bottom padding to lift a
  // focused input (e.g. the email field) above it. 0 / no-op without a keyboard.
  const keyboardInset = useKeyboardInset()

  const [visible, setVisible] = useState(true)
  const prevStepId = useRef(currentStep?.id)
  const firedShowFor = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (currentStep && currentStep.id !== prevStepId.current) {
      setVisible(false)
      const t = requestAnimationFrame(() => {
        prevStepId.current = currentStep.id
        setVisible(true)
      })
      return () => cancelAnimationFrame(t)
    }
  }, [currentStep])

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep?.id])

  // Named "shown" event (data-driven), once per step. The ref guard keeps it
  // idempotent across Strict-Mode's double-invoke and unrelated re-renders.
  useEffect(() => {
    if (currentStep?.eventOnShow && firedShowFor.current !== currentStep.id) {
      firedShowFor.current = currentStep.id
      trackEvent(currentStep.eventOnShow)
    }
  }, [currentStep])

  useEffect(() => {
    if (currentStepIndex >= PRELOAD_AT_STEP) {
      warmPayrails().catch(() => {})
    }
  }, [currentStepIndex])

  // Warm the MediaPipe hand-landmarker as soon as a quiz with a photo-capture
  // step starts, so the ~11MB model is ready by the time the user reaches it
  // (instead of loading on the result screen).
  useEffect(() => {
    if (config.steps.some((step) => step.type === "photo")) {
      ensureHandLandmarker().catch(() => {})
    }
  }, [config])

  if (!currentStep) return null

  const {
    Component: StepComponent,
    autoAdvance = false,
    fullBleed = false,
  } = resolveStepDescriptor(config, currentStep)

  const handleNext = () => {
    const completed = goNext()
    if (completed) onComplete()
  }

  const handleChange = (value: QuizAnswerValue) => {
    setCurrentAnswer(value)
    if (autoAdvance) setTimeout(handleNext, 200)
  }

  const handleSecondary = () => {
    setCurrentAnswer(currentStep.secondaryAction?.value || "unknown")
    setTimeout(handleNext, 0)
  }

  const stepProps = {
    step: currentStep,
    value: currentValue,
    onChange: handleChange,
    onAdvance: handleNext,
    onBack: goBack,
    countedCurrent,
    countedTotal,
    answers,
  }

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col overflow-x-clip bg-dark-base font-body text-purple-light"
      style={keyboardInset ? { paddingBottom: keyboardInset } : undefined}
    >
      {!fullBleed && currentStep.bottomDecoration && <QuizBottomDecoration />}

      {!fullBleed && (
        <QuizHeader
          current={countedCurrent}
          total={countedTotal}
          showCounter={currentCounts}
          showBack={!isFirst}
          onBack={goBack}
        />
      )}

      {fullBleed ? (
        <StepComponent key={currentStep.id} {...stepProps} />
      ) : (
        <div className={`relative z-10 flex flex-1 flex-col px-4 pt-8${autoAdvance ? " pb-6" : ""}`}>
          <div
            key={currentStep.id}
            className="flex flex-1 flex-col transition-all duration-200 ease-out"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(16px)",
            }}
          >
            <div className="space-y-2 text-center">
              <h1 className="font-display text-2xl font-medium text-purple-light">
                {currentStep.title}
              </h1>
              {currentStep.subtitle && (
                <p className="text-base text-purple-light/70">{currentStep.subtitle}</p>
              )}
            </div>

            <div className="mt-8 flex flex-1 flex-col">
              <StepComponent {...stepProps} />
            </div>
          </div>

          {/* Continue stays pinned to the bottom of the viewport so it's always
              reachable, even when the step's content is taller than the screen and
              scrolls. The gradient fades the scrolling content out behind it. */}
          {!autoAdvance && (
            <div className="sticky bottom-0 z-20 -mx-4 space-y-3 bg-gradient-to-t from-dark-base from-40% to-transparent px-4 pt-10 pb-6">
              <Button variant="gradient" size="xl" onClick={handleNext} disabled={!canProceed}>
                Continue
              </Button>
              {currentStep.secondaryAction && (
                <Button
                  variant="ghost"
                  size="xl"
                  onClick={handleSecondary}
                  className="bg-purple-light/10 text-purple-light hover:bg-purple-light/15"
                >
                  {currentStep.secondaryAction.label}
                </Button>
              )}
              {currentStep.legalNote && <QuizLegalNote />}
              {currentStep.reassurance && (
                <p className="flex items-center justify-center gap-1.5 text-center text-xs text-purple-light/40">
                  <ShieldCheckIcon className="size-[15px] shrink-0" />
                  {currentStep.reassurance}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
